import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { CITIES_DATA } from "@/data/citiesData";

const CountUp = ({ to, from = 0, suffix = "", decimals = 0, duration = 2000 }) => {
  const ref = useRef(null);
  const [val, setVal] = useState(from);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(from + (to - from) * eased);
            if (p < 1) requestAnimationFrame(tick);
            else setVal(to);
          };
          requestAnimationFrame(tick);
        } else {
          setVal(from);
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [to, from, duration]);

  return (
    <span ref={ref} data-testid="countup-value">
      {val.toFixed(decimals)}
      {suffix}
    </span>
  );
};

const POINTS = [
  "Local technicians who show up on time and clean up after the job",
  "Authorized Verkada Dealer — commercial-grade cloud cameras and access control",
  "3-year warranty on equipment and workmanship",
  "Honest, written quotes — no surprise charges, ever",
  "One team for security, cabling, AV, and networking",
  "Ongoing support after installation — we answer our phone",
];

const STATS = [
  { value: 500, from: 200, suffix: "+", label: "Happy clients" },
  { value: 5, from: 0, decimals: 1, label: "Google rating" },
  { value: 10, from: 0, suffix: "+", label: "Years of experience" },
  { text: "Zero Hassle", label: "Free On-Site Quotes" },
];

export const About = () => {
  return (
    <section id="about" data-testid="about-section" className="py-24 lg:py-32 bg-white">
      <div className="container-x grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.div
            initial={{ y: 24 }}
            whileInView={{ y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-4">
            About Us
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-zinc-900">
            Why New Brunswick trusts SafeHome
          </h2>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="origin-left mt-5 h-1 w-20 rounded-full bg-gradient-to-r from-[#8C6D1F] via-[#D4AF37] to-[#E9C767]"
          />
          </motion.div>
          <p className="mt-6 text-base leading-relaxed text-zinc-600">
            SafeHome Security Solutions Inc. is a locally owned installation company serving homeowners and
            businesses across New Brunswick — from Moncton and Saint John to Fredericton and everywhere in
            between. We treat every property like our own: careful wiring, tidy work, and systems set up
            properly the first time.
          </p>
          <p className="mt-4 text-base leading-relaxed text-zinc-600">
            As an authorized Verkada dealer, we bring the same cloud-based security technology trusted by
            schools, hospitals, and businesses across North America to your home or workplace — and we back
            every installation with a 3-year warranty. Your security is our priority.
          </p>
          <ul className="mt-8 space-y-4">
            {POINTS.map((point) => (
              <li key={point} className="flex items-start gap-3 text-zinc-700">
                <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-[#D4AF37]" />
                <span className="text-sm sm:text-base leading-relaxed">{point}</span>
              </li>
            ))}
          </ul>
          <nav
            className="mt-10 flex flex-wrap gap-x-3 gap-y-1.5 text-[13px] uppercase tracking-[0.14em] font-semibold text-[#9A7B1E]"
            aria-label="Service areas"
            data-testid="about-cities-line"
          >
            {CITIES_DATA.map((c, i) => (
              <span key={c.slug} className="flex items-center gap-3">
                <Link to={`/service-areas/${c.slug}/`} className="underline underline-offset-4 decoration-[#D4AF37]/50 hover:text-zinc-900 transition-colors">
                  {c.name}
                </Link>
                {i < CITIES_DATA.length - 1 && <span className="text-[#D4AF37]" aria-hidden="true">•</span>}
              </span>
            ))}
          </nav>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {STATS.map((stat) => (
            <div
              key={stat.label}
              data-testid={`stat-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="bg-[#FAFAFA] border border-zinc-200 rounded-md p-8 shadow-sm"
            >
              <p className={`${stat.text ? "text-2xl lg:text-3xl" : "text-3xl lg:text-4xl"} font-extrabold tracking-tight text-zinc-900`}>
                {stat.text ? (
                  stat.text
                ) : (
                  <CountUp to={stat.value} from={stat.from || 0} suffix={stat.suffix || ""} decimals={stat.decimals || 0} />
                )}
              </p>
              <p className="mt-2 text-sm text-zinc-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
